'use client';

import GetSlash from '@modules/get-slash-internet-card/lib/get-slash';
import { logger } from '@tuic/logger';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { GetSlashCredentials } from '@lib/third-parties/get-slash.types';
import type { GetSlashTariff } from '@modules/get-slash-internet-card/lib/get-slash';

/**
 * State returned by the useBookableTariffs hook
 */
export type BookableTariffsState = {
  /** List of available tariffs */
  tariffs: GetSlashTariff[];
  /** Function to get a specific tariff by ID */
  getTariff: (id: number) => GetSlashTariff | undefined;
  /** Function to book a tariff by ID */
  book: (id: number) => Promise<boolean>;
  /** Function to refresh the list of tariffs */
  refreshTariffs: () => Promise<void>;
  /** Whether the tariffs have been loaded */
  ready: boolean;
};

/**
 * Hook for fetching and booking internet tariffs.
 * Provides functions to get tariff details, book tariffs, and refresh the tariff list.
 */
export const useBookableTariffs = (args: {
  credentials: GetSlashCredentials | undefined;
  useDebugEndpoints?: boolean;
}): BookableTariffsState => {
  const { credentials, useDebugEndpoints } = args;

  const [tariffs, setTariffs] = useState<GetSlashTariff[]>([]);
  const [ready, setReady] = useState<boolean>(false);

  const controller = useMemo(() => {
    if (typeof credentials === 'undefined') {
      return undefined;
    }

    return new GetSlash({
      credentials,
      useDebugEndpoints,
    });
  }, [credentials, useDebugEndpoints]);

  const refreshTariffs = useCallback(async () => {
    if (typeof controller === 'undefined') {
      return;
    }

    try {
      const response = await controller.tariffList();

      if (!response.success) {
        setTariffs([]);
        return;
      }

      setTariffs(response.tariffs ?? []);
    } catch (err) {
      logger.error(err);
    }

    setReady(true);
  }, [controller]);

  // Fetch tariffs on mount and when controller changes
  useEffect(() => {
    refreshTariffs();
  }, [refreshTariffs]);

  const getTariff = useCallback(
    (id: number) => {
      return tariffs.find(tariff => tariff.id === id);
    },
    [tariffs],
  );

  const book = useCallback(
    async (id: number) => {
      if (typeof controller === 'undefined') {
        return false;
      }

      // Check if the tariff exists
      const tariff = getTariff(id);

      if (!tariff) {
        return false;
      }

      try {
        // Call the bookingAdd method with the tariff ID
        const { success } = await controller.bookingAdd({
          tariffId: id,
        });

        return success;
      } catch {
        // Silently handle the error and return false
        return false;
      }
    },
    [controller, getTariff],
  );

  return {
    tariffs,
    getTariff,
    book,
    refreshTariffs,
    ready,
  };
};
